import { useMemo, useState } from "react";
import { Copy, Check, Phone, CheckCircle2, FileText, Ban, AlertTriangle, MapPin, Users, Sparkles } from "lucide-react";
import { montarRelatorio, relatorioEmTexto } from "../lib/relatorioDiario";
import { fmtDate } from "../utils";

// ═══════════════════════════════════════════════════════════════
// RELATÓRIO DO DIA
// ═══════════════════════════════════════════════════════════════
//
// Responde as três perguntas que a gerência faz todo fim de tarde, nesta
// ordem: o que aconteceu, quem fez, o que ficou para trás. Antes eram KPIs de
// pipeline soltos — verdadeiros, mas nenhum deles falava do DIA.
//
// Nada aqui é digitado: sai da trilha. O botão de copiar entrega o mesmo
// conteúdo em texto, para colar no grupo — que é como o relatório de fato
// circula na equipe.

export default function RelatorioDoDia({ partners = [], credenciamentos = [], satisfaction = [], metas = [], dia }) {
  const [copiado, setCopiado] = useState(false);
  const r = useMemo(
    () => montarRelatorio({ partners, credenciamentos, satisfaction, metas, dia }),
    [partners, credenciamentos, satisfaction, metas, dia]
  );
  const a = r.aconteceu;

  const copiar = async () => {
    try {
      await navigator.clipboard.writeText(relatorioEmTexto(r));
      setCopiado(true);
      setTimeout(() => setCopiado(false), 2500);
    } catch { /* navegador sem permissão de área de transferência */ }
  };

  const vazio = a.contatos === 0 && a.aceites === 0 && a.novasEmpresas === 0 && a.fichasCriadas === 0;

  return (
    <div style={{ background: "#fff", borderRadius: 10, border: "1px solid #e5e7eb", padding: "18px 20px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
        <div>
          <div style={{ fontSize: 15, fontWeight: 800, color: "#0f172a" }}>Relatório do dia</div>
          <div style={{ fontSize: 11, color: "#64748b" }}>{fmtDate(r.dia)} — tudo derivado da trilha, nada digitado</div>
        </div>
        <button onClick={copiar} style={{ display: "flex", alignItems: "center", gap: 6, padding: "8px 15px", borderRadius: 7, border: "none", background: copiado ? "#059669" : "#7c3aed", color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>
          {copiado ? <><Check size={13} /> Copiado</> : <><Copy size={13} /> Copiar para o grupo</>}
        </button>
      </div>

      {vazio && (
        <div style={{ padding: "14px", borderRadius: 8, background: "#fffbeb", border: "1px solid #fde68a", fontSize: 12, color: "#92400e", marginBottom: 14 }}>
          Nenhum contato, aceite ou cadastro registrado hoje até agora.
        </div>
      )}

      {/* 1. O QUE ACONTECEU */}
      <Secao titulo="O que aconteceu">
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(130px, 1fr))", gap: 8 }}>
          <Num icone={<Phone size={13} />} cor="#7c3aed" valor={a.contatos} label="contatos" nota={`${a.comProva} com print`} />
          <Num icone={<Users size={13} />} cor="#6366f1" valor={a.novasEmpresas} label="empresas novas" />
          <Num icone={<CheckCircle2 size={13} />} cor="#0d9488" valor={a.aceites} label="aceites" />
          <Num icone={<FileText size={13} />} cor="#f59e0b" valor={a.fichasConcluidas} label="fichas concluídas" nota={a.fichasCriadas ? `${a.fichasCriadas} criadas` : ""} />
          <Num icone={<CheckCircle2 size={13} />} cor="#059669" valor={a.ativacoes} label="ativações" />
          <Num icone={<Ban size={13} />} cor="#d97706" valor={a.recusas} label="recusas" />
        </div>
        {a.empresasAceitaram.length > 0 && (
          <div style={{ fontSize: 11.5, color: "#0f766e", marginTop: 8 }}>Aceitaram hoje: <strong>{a.empresasAceitaram.join(", ")}</strong></div>
        )}
        {a.avancosAuto > 0 && (
          <div style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 10.5, color: "#64748b", marginTop: 6 }}>
            <Sparkles size={11} color="#7c3aed" /> {a.avancosAuto} {a.avancosAuto === 1 ? "empresa avançou" : "empresas avançaram"} de estágio sozinhas (documentação completa ou cadastro no sistema)
          </div>
        )}
      </Secao>

      {/* 2. QUEM FEZ */}
      <Secao titulo="Quem fez">
        {r.pessoas.length === 0 && <Vazio>Ninguém registrou atividade hoje.</Vazio>}
        {r.pessoas.map((p) => (
          <div key={p.autor} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, padding: "7px 0", borderBottom: "1px solid #f8fafc", fontSize: 12 }}>
            <span style={{ color: "#334155", fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{p.autor || "sem autor"}</span>
            <span style={{ display: "flex", alignItems: "center", gap: 10, whiteSpace: "nowrap" }}>
              {p.aceites > 0 && <span style={{ fontSize: 10.5, color: "#0f766e" }}>{p.aceites} aceite{p.aceites > 1 ? "s" : ""}</span>}
              {p.comProva < p.contatos && (
                <span style={{ fontSize: 10.5, color: "#b45309" }}>{p.contatos - p.comProva} sem print</span>
              )}
              <span style={{ fontSize: 11.5, fontWeight: 700, color: p.bateu ? "#059669" : "#dc2626" }}>
                {p.contatos}/{p.meta}
              </span>
            </span>
          </div>
        ))}
      </Secao>

      {/* 3. ONDE */}
      {r.regioes.length > 0 && (
        <Secao titulo="Onde">
          {r.regioes.map((g) => (
            <div key={g.uf} style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "5px 0", fontSize: 11.5, borderBottom: "1px solid #f8fafc" }}>
              <span style={{ color: "#334155" }}><MapPin size={11} color="#7c3aed" style={{ verticalAlign: "-1px", marginRight: 4 }} />{g.uf}</span>
              <span style={{ color: "#64748b" }}>
                <strong style={{ color: "#0f172a" }}>{g.total}</strong> — {g.cidades.slice(0, 4).map((c) => `${c.cidade} (${c.total})`).join(", ")}
              </span>
            </div>
          ))}
        </Secao>
      )}

      {/* 4. O QUE FICOU */}
      <Secao titulo="O que ficou para trás">
        {r.ficou.parados.length === 0 && !r.ficou.aConferir && !r.ficou.semProva && !r.ficou.pesquisaVencida && (
          <Vazio>Nada pendente. Dia limpo.</Vazio>
        )}
        {r.ficou.parados.length > 0 && (
          <Pend cor="#dc2626" texto={`${r.ficou.parados.length} em abordagem parada`} detalhe={r.ficou.parados.slice(0, 3).map((p) => `${p.nome} (${p.dias}d)`).join(", ")} />
        )}
        {r.ficou.semProva > 0 && <Pend cor="#b45309" texto={`${r.ficou.semProva} contato(s) de hoje sem print`} />}
        {r.ficou.aConferir > 0 && <Pend cor="#7c3aed" texto={`${r.ficou.aConferir} registro(s) esperando conferência da gerência`} />}
        {r.ficou.pesquisaVencida > 0 && <Pend cor="#0284c7" texto={`${r.ficou.pesquisaVencida} parceiro(s) ativo(s) sem pesquisa há mais de 3 meses`} />}
      </Secao>
    </div>
  );
}

function Secao({ titulo, children }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 8 }}>{titulo}</div>
      {children}
    </div>
  );
}

function Num({ icone, cor, valor, label, nota }) {
  return (
    <div style={{ padding: "10px 12px", borderRadius: 8, background: "#f8fafc", border: "1px solid #e2e8f0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 5, color: cor }}>
        {icone}<span style={{ fontSize: 19, fontWeight: 800 }}>{valor}</span>
      </div>
      <div style={{ fontSize: 10.5, color: "#64748b", fontWeight: 600 }}>{label}</div>
      {nota && <div style={{ fontSize: 9.5, color: "#94a3b8" }}>{nota}</div>}
    </div>
  );
}

function Pend({ cor, texto, detalhe }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "5px 0", fontSize: 11.5, color: "#334155" }}>
      <AlertTriangle size={12} color={cor} style={{ flexShrink: 0, marginTop: 2 }} />
      <span>{texto}{detalhe ? <span style={{ color: "#94a3b8" }}> — {detalhe}</span> : null}</span>
    </div>
  );
}

function Vazio({ children }) {
  return <div style={{ fontSize: 11.5, color: "#94a3b8" }}>{children}</div>;
}
